import type { MMU } from '../mmu/MMU';
import type { InterruptController } from '../interrupt/InterruptController';
import { createRegisterBank, type RegisterBank } from './registers';
import { buildOpcodeTable, type OpcodeEntry } from './opcode-table';
import { H_TI, H_DS, H_WB } from './instructions/constants';
import { PSW_Z, PSW_MIE } from './instructions/constants';

export interface CPUOptions {
  mmu: MMU;
  interruptController: InterruptController;
}

export interface CPUSnapshot {
  registers: {
    r: number[];
    cr: number[];
    pc: number;
    csr: number;
    sp: number;
    ea: number;
    dsr: number;
    psw: number;
    elr: number[];
    ecsr: number[];
    epsw: number[];
  };
  cycleCount: number;
}

export class CPU {
  readonly mmu: MMU;
  readonly interruptController: InterruptController;
  readonly regs: RegisterBank;

  impl_opcode: number = 0;
  impl_long_imm: number = 0;
  impl_hint: number = 0;
  impl_flags_in: number = 0;
  impl_flags_out: number = 0;
  impl_flags_changed: number = 0;
  impl_shift_buffer: number = 0;
  impl_last_dsr: number = 0;
  impl_operands: Array<{ value: number; register_index: number; register_size: number }> = [
    { value: 0, register_index: 0, register_size: 0 },
    { value: 0, register_index: 0, register_size: 0 },
  ];

  private opcodeTable: Map<number, OpcodeEntry> | null = null;
  private _cycleCount: number = 0;
  private _halted: boolean = false;

  constructor(options: CPUOptions) {
    this.mmu = options.mmu;
    this.interruptController = options.interruptController;
    this.regs = createRegisterBank();
  }

  step(): void {
    if (this._halted)
      return;

    this.regs.dsr = 0;

    let _safety = 1000;
    while (true) {
      if (--_safety <= 0) break;
      this.impl_opcode = this.fetchInstruction();

      const entry = this.getOpcodeTable().get(this.impl_opcode);
      if (!entry)
        continue;

      this.impl_long_imm = 0;
      if (entry.hint & H_TI)
        this.impl_long_imm = this.fetchInstruction();

      for (let ix = 0; ix < 2; ix++) {
        const operand = entry.operands[ix];
        this.impl_operands[ix].value = (this.impl_opcode >> operand.shift) & operand.mask;
        this.impl_operands[ix].register_index = this.impl_operands[ix].value;
        this.impl_operands[ix].register_size = operand.register_size;

        if (operand.register_size) {
          this.impl_operands[ix].value = 0;
          for (let bx = 0; bx < operand.register_size; bx++)
            this.impl_operands[ix].value |= this.regs.r[this.impl_operands[ix].register_index + bx] << (bx * 8);
        }
      }
      this.impl_hint = entry.hint;

      this.impl_flags_changed = 0;
      this.impl_flags_in = this.regs.psw;
      this.impl_flags_out = PSW_Z;

      entry.handler(this);

      this.regs.psw &= ~this.impl_flags_changed;
      this.regs.psw |= this.impl_flags_out & this.impl_flags_changed;

      if (entry.hint & H_WB && this.impl_operands[0].register_size) {
        for (let bx = 0; bx < this.impl_operands[0].register_size; bx++)
          this.regs.r[this.impl_operands[0].register_index + bx] = (this.impl_operands[0].value >> (bx * 8)) & 0xFF;
      }

      if (!(entry.hint & H_DS))
        break;
    }

    this._cycleCount++;
  }

  private fetchInstruction(): number {
    if (this.regs.csr & ~0x0F)
      this.regs.csr &= 0x0F;
    if (this.regs.pc & 1)
      this.regs.pc &= ~1;
    const addr = ((this.regs.csr << 16) | this.regs.pc) & 0xFFFFF;
    const opcode = this.mmu.readCode16(addr);
    this.regs.pc = (this.regs.pc + 2) & 0xFFFF;
    return opcode;
  }

  private getOpcodeTable(): Map<number, OpcodeEntry> {
    if (!this.opcodeTable) {
      this.opcodeTable = buildOpcodeTable();
    }
    return this.opcodeTable;
  }

  reset(): void {
    const regs = this.regs;
    regs.r.fill(0);
    regs.cr.fill(0);
    regs.pc = 0;
    regs.csr = 0;
    regs.sp = this.mmu.readCode(0) | (this.mmu.readCode(1) << 8);
    regs.ea = 0;
    regs.dsr = 0;
    regs.psw = 0;
    regs.elr = [0, 0, 0, 0];
    regs.ecsr = [0, 0, 0, 0];
    regs.epsw = [0, 0, 0, 0];
    this._cycleCount = 0;
    this._halted = false;
    this.impl_last_dsr = 0;
  }

  raiseException(exceptionLevel: number, index: number): void {
    if (exceptionLevel === 1)
      this.regs.psw &= ~PSW_MIE;
    this.regs.psw = (this.regs.psw & ~0x03) | exceptionLevel;

    this.regs.elr[exceptionLevel] = this.regs.pc;
    this.regs.ecsr[exceptionLevel] = this.regs.csr;

    this.regs.csr = 0;
    this.regs.pc = this.mmu.readCode16(index * 2);
  }

  getCycleCount(): number {
    return this._cycleCount;
  }

  getCodeAddress(): number {
    return ((this.regs.csr << 16) | (this.regs.pc & 0xFFFF)) & 0xFFFFF;
  }

  isHalted(): boolean {
    return this._halted;
  }

  setHalted(halted: boolean): void {
    this._halted = halted;
  }

  saveSnapshot(): CPUSnapshot {
    return {
      registers: {
        r: Array.from(this.regs.r),
        cr: Array.from(this.regs.cr),
        pc: this.regs.pc,
        csr: this.regs.csr,
        sp: this.regs.sp,
        ea: this.regs.ea,
        dsr: this.regs.dsr,
        psw: this.regs.psw,
        elr: [...this.regs.elr],
        ecsr: [...this.regs.ecsr],
        epsw: [...this.regs.epsw],
      },
      cycleCount: this._cycleCount,
    };
  }

  loadSnapshot(snapshot: CPUSnapshot): void {
    const r = snapshot.registers;
    this.regs.r.set(r.r);
    this.regs.cr.set(r.cr);
    this.regs.pc = r.pc;
    this.regs.csr = r.csr;
    this.regs.sp = r.sp;
    this.regs.ea = r.ea;
    this.regs.dsr = r.dsr;
    this.regs.psw = r.psw;
    this.regs.elr = r.elr as [number, number, number, number];
    this.regs.ecsr = r.ecsr as [number, number, number, number];
    this.regs.epsw = r.epsw as [number, number, number, number];
    this._cycleCount = snapshot.cycleCount;
  }
}
